import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SITE } from "@/lib/site";
import SectionHeading from "@/components/SectionHeading";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent.");
    } catch {
      toast.error("Couldn't send. Try calling instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24 px-6 md:px-10">
      <div className="mx-auto max-w-[1400px]">
        <SectionHeading
          eyebrow="Get in Touch"
          title="Drop us a line."
          subtitle="Questions, group bookings, private events — we read every message."
          testIdBase="contact-header"
        />

        <div className="grid lg:grid-cols-12 gap-10 mt-14">
          <div className="lg:col-span-5 space-y-8">
            <InfoBlock icon={<MapPin className="w-5 h-5" />} label="Studio" lines={[SITE.address]} />
            <InfoBlock icon={<Phone className="w-5 h-5" />} label="Phone" lines={[SITE.phone]} href={SITE.phoneHref} />
            <InfoBlock icon={<Mail className="w-5 h-5" />} label="Email" lines={[SITE.email]} href={`mailto:${SITE.email}`} />
            <InfoBlock icon={<Clock className="w-5 h-5" />} label="Hours" lines={SITE.hours.map((h) => `${h.day} — ${h.time}`)} />
          </div>

          <div className="lg:col-span-7">
            <div className="card-monarch p-8 md:p-10">
              {sent ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <h3 className="font-serif text-3xl text-monarch-cream mb-3">Got it.</h3>
                  <p className="text-monarch-ash">We&rsquo;ll be in touch shortly.</p>
                  <button onClick={() => { setSent(false); setForm({ name: "", email: "", message: "" }); }} className="btn-outline-gold mt-8" data-testid="contact-reset">Send another</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-7">
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.3em] text-monarch-gold mb-2 block">Name</span>
                      <input data-testid="contact-input-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-monarch" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.3em] text-monarch-gold mb-2 block">Email</span>
                      <input data-testid="contact-input-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-monarch" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-monarch-gold mb-2 block">Message</span>
                    <textarea data-testid="contact-input-message" rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-monarch resize-none" />
                  </label>
                  <button data-testid="contact-submit" disabled={submitting} className="btn-gold">
                    {submitting ? "Sending…" : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Google Maps + Reviews */}
        <section data-testid="contact-map-section" className="mt-20">
          <div className="grid lg:grid-cols-12 gap-10 items-stretch">
            <div className="lg:col-span-7 border border-monarch-gold/20 overflow-hidden bg-monarch-coal aspect-[16/10] lg:aspect-auto lg:min-h-[420px]">
              <iframe
                data-testid="google-map-embed"
                title="Monarch Cutz location"
                src={SITE.reviews.googleEmbedNoKey}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.6) contrast(1.05) brightness(0.85)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>

            <div className="lg:col-span-5 card-monarch p-8 md:p-10 flex flex-col justify-center">
              <div className="eyebrow mb-5">Reviews</div>
              <h3 className="font-serif text-3xl md:text-4xl text-monarch-cream leading-tight">Hear it from the chair.</h3>
              <p className="mt-4 text-monarch-ash leading-relaxed text-sm">
                Walk out happy? Share the love. A few seconds on Google goes a long way for a small shop — and a few seconds on Reddit goes even further with the locals.
              </p>
              <div className="flex flex-col gap-3 mt-7">
                <a
                  href={SITE.reviews.google}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="google-review-cta"
                  className="btn-gold justify-between"
                >
                  <span className="flex items-center gap-3">
                    <Star className="w-4 h-4 fill-monarch-black" /> Leave a review on Google
                  </span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <a
                  href={SITE.reviews.viewOnGoogle}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="view-on-google-cta"
                  className="btn-outline-gold justify-between"
                >
                  <span>View us on Google Maps</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <a
                  href={SITE.social.reddit.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="reddit-cta"
                  className="btn-outline-gold justify-between"
                >
                  <span>Discuss on {SITE.social.reddit.handle}</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function InfoBlock({ icon, label, lines, href }) {
  const content = (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-monarch-gold mb-1.5">{label}</div>
      {lines.map((l, i) => (<div key={i} className="text-monarch-cream/85 text-sm leading-relaxed">{l}</div>))}
    </div>
  );
  return (
    <div className="flex gap-5 items-start">
      <div className="w-11 h-11 border border-monarch-gold/30 text-monarch-gold flex items-center justify-center shrink-0">{icon}</div>
      {href ? <a href={href} className="hover:opacity-80 transition-opacity">{content}</a> : content}
    </div>
  );
}
